import type { ReactNode } from 'react';
import { useCallback, useRef } from 'react';

import { Dropdown } from 'antd';
import clsx from 'clsx';

export interface DropdownMenuItem {
  key: string;
  title: string;
  description?: string;
  href?: string;
  target?: string;
  disabled?: boolean;
  onClick?: () => void;
  /** 仅对内显示，外部域名下会被过滤掉 */
  internalOnly?: boolean;
}

interface DropdownHeader {
  title: string;
  description?: string;
}

interface CustomDropdownProps {
  items: DropdownMenuItem[];
  children: ReactNode;
  width?: number | string;
  className?: string;
  placement?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'top' | 'topLeft' | 'topRight';
  trigger?: ('click' | 'hover' | 'contextMenu')[];
  dropdownClassName?: string;
  header?: DropdownHeader;
  onOpenChange?: (open: boolean) => void;
}

export function CustomDropdown({
  items,
  children,
  width = 480,
  className,
  placement = 'bottomLeft',
  trigger = ['hover'],
  header,
  onOpenChange,
}: CustomDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 阻止滚动穿透到主页面
  const handleWheel = useCallback((e: globalThis.WheelEvent) => {
    const el = dropdownRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const deltaY = e.deltaY;

    // 如果内容不可滚动，直接阻止
    if (scrollHeight <= clientHeight) {
      e.preventDefault();
      return;
    }

    // 到达顶部且向上滚动，或到达底部且向下滚动时，阻止滚动穿透
    if (
      (scrollTop === 0 && deltaY < 0) ||
      (scrollTop + clientHeight >= scrollHeight && deltaY > 0)
    ) {
      e.preventDefault();
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    const el = dropdownRef.current;
    if (el) {
      el.addEventListener('wheel', handleWheel, { passive: false });
    }
  }, [handleWheel]);

  const handleMouseLeave = useCallback(() => {
    const el = dropdownRef.current;
    if (el) {
      el.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  const renderDropdown = () => (
    <div
      ref={dropdownRef}
      className={clsx(
        'text-content-primary flex flex-col items-start p-2',
        'border-border-primary rounded-[6px]',
        'bg-background-primary shadow-[0_4px_15px_0_rgba(0,0,0,0.1)]',
        'max-h-[80vh] overflow-y-auto overscroll-contain',
      )}
      style={{ width }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {header && (
        <>
          <div className="w-full p-2">
            <div className="font-medium">{header.title}</div>
            {header.description && (
              <div className="mt-1 text-sm opacity-50">{header.description}</div>
            )}
          </div>
          <div className="bg-border-primary my-1 h-px w-full" />
        </>
      )}
      <div className="flex w-full flex-col gap-1">
        {items.map((item) => {
          const content = (
            <div className="flex flex-col gap-1">
              <div className="font-normal">{item.title}</div>
              {item.description && <div className="text-sm opacity-50">{item.description}</div>}
            </div>
          );

          const itemClassName = clsx(
            'w-full cursor-pointer rounded-[4px] p-2 transition-colors duration-200',
            'can-hover:hover:bg-background-hover/40',
            item.disabled && 'cursor-not-allowed opacity-50',
          );

          const handleClick = () => {
            if (item.disabled) return;

            if (item.onClick) {
              item.onClick();
            }

            if (item.href) {
              if (item.target === '_blank') {
                window.open(item.href, '_blank', 'noopener,noreferrer');
              } else {
                window.location.href = item.href;
              }
            }
          };

          return (
            <div key={item.key} className={itemClassName} onClick={handleClick}>
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Dropdown
      trigger={trigger}
      placement={placement}
      popupRender={renderDropdown}
      className={className}
      onOpenChange={onOpenChange}
    >
      {children}
    </Dropdown>
  );
}
